import {
  useCallback,
  useEffect,
  useState
} from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Dumbbell,
  Library,
  Loader2,
  Pencil,
  Plus,
  Timer,
  Upload
} from 'lucide-react';

const formInicial = {
  nome: '',
  descricao: '',
  instrucoes: '',
  duracao_padrao: '30',
  descanso_padrao: '15',
  gif_url: '',
  ativo: true
};

export default function TreinosCasaPage() {
  const [exercicios, setExercicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(formInicial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.get(
        '/gestor/exercicios-casa',
        {
          params: {
            incluir_inativos: true
          }
        }
      );

      setExercicios(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setEditing(null);
    setForm(formInicial);
    setDialogOpen(true);
  };

  const abrirEditar = exercicio => {
    setEditing(exercicio);

    setForm({
      nome: exercicio.nome || '',
      descricao: exercicio.descricao || '',
      instrucoes: exercicio.instrucoes || '',
      duracao_padrao: String(
        exercicio.duracao_padrao || 30
      ),
      descanso_padrao: String(
        exercicio.descanso_padrao ?? 15
      ),
      gif_url: exercicio.gif_url || '',
      ativo: exercicio.ativo !== false
    });

    setDialogOpen(true);
  };

  const atualizarCampo = (campo, valor) => {
    setForm(atual => ({
      ...atual,
      [campo]: valor
    }));
  };

  const enviarGif = async event => {
    const file = event.target.files?.[0];

    event.target.value = '';

    if (!file) {
      return;
    }

    if (
      file.type !== 'image/gif'
      && !file.name.toLowerCase().endsWith('.gif')
    ) {
      toast.error('Selecione um arquivo GIF');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('O GIF deve ter no máximo 8 MB');
      return;
    }

    setUploading(true);

    try {
      const dados = new FormData();
      dados.append('file', file);

      const res = await api.post(
        '/gestor/exercicios-casa/upload-gif',
        dados,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      atualizarCampo(
        'gif_url',
        res.data?.gif_url || ''
      );

      toast.success('GIF enviado');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const salvar = async event => {
    event.preventDefault();

    const nome = form.nome.trim();
    const duracao = Number(
      form.duracao_padrao
    );
    const descanso = Number(
      form.descanso_padrao
    );

    if (!nome) {
      toast.error(
        'Informe o nome do exercício'
      );
      return;
    }

    if (
      !Number.isInteger(duracao)
      || duracao <= 0
    ) {
      toast.error(
        'A duração deve ser maior que zero'
      );
      return;
    }

    if (
      !Number.isInteger(descanso)
      || descanso < 0
    ) {
      toast.error(
        'O descanso não pode ser negativo'
      );
      return;
    }

    const payload = {
      nome,
      descricao: form.descricao.trim(),
      instrucoes: form.instrucoes.trim(),
      duracao_padrao: duracao,
      descanso_padrao: descanso,
      gif_url: form.gif_url,
      ativo: form.ativo
    };

    setSaving(true);

    try {
      if (editing) {
        await api.put(
          `/gestor/exercicios-casa/${editing.id}`,
          payload
        );

        toast.success(
          'Exercício atualizado'
        );
      } else {
        await api.post(
          '/gestor/exercicios-casa',
          payload
        );

        toast.success(
          'Exercício criado'
        );
      }

      setDialogOpen(false);
      setEditing(null);
      setForm(formInicial);

      await carregar();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="gestor-treinos-casa-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              backgroundColor:
                'color-mix(in srgb, var(--cor-primaria, #007AFF) 15%, transparent)'
            }}
          >
            <Dumbbell
              className="w-5 h-5"
              style={{
                color:
                  'var(--cor-primaria, #007AFF)'
              }}
            />
          </div>

          <div>
            <h1 className="text-2xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
              TREINO EM CASA
            </h1>

            <p className="text-sm text-zinc-500">
              Monte atividades para seus atletas
            </p>
          </div>
        </div>

        <Button
          onClick={abrirNovo}
          style={{
            backgroundColor:
              'var(--cor-primaria, #007AFF)'
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo exercício
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 p-1 bg-[#0F0F0F] border border-[#27272A] rounded-xl">
        <div className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-zinc-500">
          <Dumbbell className="w-4 h-4" />
          Treinos
          <Badge
            variant="outline"
            className="ml-1 text-[9px] border-[#303036]"
          >
            em breve
          </Badge>
        </div>

        <div
          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold text-white"
          style={{
            backgroundColor:
              'var(--cor-primaria, #007AFF)'
          }}
        >
          <Library className="w-4 h-4" />
          Exercícios
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Carregando exercícios...
        </div>
      ) : exercicios.length === 0 ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-10 text-center">
            <Library className="w-10 h-10 text-zinc-600 mx-auto mb-3" />

            <p className="font-semibold text-white">
              Sua biblioteca está vazia
            </p>

            <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
              Cadastre os exercícios que serão usados
              para montar os treinos dos atletas.
            </p>

            <Button
              onClick={abrirNovo}
              className="mt-5"
              style={{
                backgroundColor:
                  'var(--cor-primaria, #007AFF)'
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar primeiro exercício
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {exercicios.map(exercicio => (
            <Card
              key={exercicio.id}
              className={`overflow-hidden border ${
                exercicio.ativo
                  ? 'bg-[#141414] border-[#27272A]'
                  : 'bg-[#101010] border-[#202020] opacity-70'
              }`}
            >
              {exercicio.gif_url ? (
                <div className="aspect-video bg-black">
                  <img
                    src={exercicio.gif_url}
                    alt={exercicio.nome}
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-[#0A0A0A] flex items-center justify-center">
                  <Dumbbell className="w-12 h-12 text-zinc-700" />
                </div>
              )}

              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-white truncate">
                      {exercicio.nome}
                    </h3>

                    {exercicio.descricao && (
                      <p className="text-sm text-zinc-500 mt-1 line-clamp-2">
                        {exercicio.descricao}
                      </p>
                    )}
                  </div>

                  <Badge
                    className={
                      exercicio.ativo
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20'
                    }
                  >
                    {exercicio.ativo
                      ? 'Ativo'
                      : 'Inativo'}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-[#0F0F0F] rounded-lg p-3">
                    <div className="flex items-center gap-2 text-zinc-500">
                      <Timer className="w-4 h-4" />
                      <span className="text-xs">
                        Exercício
                      </span>
                    </div>

                    <p className="font-bold text-white mt-1">
                      {exercicio.duracao_padrao}s
                    </p>
                  </div>

                  <div className="bg-[#0F0F0F] rounded-lg p-3">
                    <div className="flex items-center gap-2 text-zinc-500">
                      <Timer className="w-4 h-4" />
                      <span className="text-xs">
                        Descanso
                      </span>
                    </div>

                    <p className="font-bold text-white mt-1">
                      {exercicio.descanso_padrao}s
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  onClick={() =>
                    abrirEditar(exercicio)
                  }
                  className="w-full mt-4 border-[#303036]"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      >
        <DialogContent className="bg-[#141414] border-[#27272A] text-white max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">
              {editing
                ? 'Editar exercício'
                : 'Novo exercício'}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={salvar}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label>Nome *</Label>

              <Input
                value={form.nome}
                onChange={e =>
                  atualizarCampo(
                    'nome',
                    e.target.value
                  )
                }
                placeholder="Ex.: Agachamento"
                className="bg-[#0F0F0F] border-[#303036]"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>

              <Textarea
                value={form.descricao}
                onChange={e =>
                  atualizarCampo(
                    'descricao',
                    e.target.value
                  )
                }
                placeholder="Resumo do exercício"
                className="bg-[#0F0F0F] border-[#303036]"
              />
            </div>

            <div className="space-y-2">
              <Label>Como executar</Label>

              <Textarea
                value={form.instrucoes}
                onChange={e =>
                  atualizarCampo(
                    'instrucoes',
                    e.target.value
                  )
                }
                placeholder="Explique a execução para o atleta"
                className="bg-[#0F0F0F] border-[#303036]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Duração (segundos)</Label>

                <Input
                  type="number"
                  min="1"
                  value={form.duracao_padrao}
                  onChange={e =>
                    atualizarCampo(
                      'duracao_padrao',
                      e.target.value
                    )
                  }
                  className="bg-[#0F0F0F] border-[#303036]"
                />
              </div>

              <div className="space-y-2">
                <Label>Descanso (segundos)</Label>

                <Input
                  type="number"
                  min="0"
                  value={form.descanso_padrao}
                  onChange={e =>
                    atualizarCampo(
                      'descanso_padrao',
                      e.target.value
                    )
                  }
                  className="bg-[#0F0F0F] border-[#303036]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>GIF demonstrativo</Label>

              <label className="flex items-center justify-center gap-2 border border-dashed border-[#3f3f46] rounded-xl p-4 cursor-pointer hover:bg-[#181818] transition-colors">
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}

                <span className="text-sm text-zinc-400">
                  {uploading
                    ? 'Enviando...'
                    : 'Selecionar GIF'}
                </span>

                <input
                  type="file"
                  accept="image/gif,.gif"
                  onChange={enviarGif}
                  disabled={uploading}
                  className="hidden"
                />
              </label>

              <p className="text-xs text-zinc-600">
                Somente GIF, máximo de 8 MB.
              </p>

              {form.gif_url && (
                <div className="aspect-video bg-black rounded-xl overflow-hidden border border-[#27272A]">
                  <img
                    src={form.gif_url}
                    alt="Prévia do exercício"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>

            {editing && (
              <div className="flex items-center justify-between gap-4 p-3 bg-[#0F0F0F] rounded-xl">
                <div>
                  <p className="text-sm font-semibold">
                    Exercício ativo
                  </p>

                  <p className="text-xs text-zinc-600">
                    Exercícios inativos não aparecem
                    normalmente na biblioteca.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    atualizarCampo(
                      'ativo',
                      !form.ativo
                    )
                  }
                  className="border-[#303036]"
                >
                  {form.ativo
                    ? 'Desativar'
                    : 'Ativar'}
                </Button>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setDialogOpen(false)
                }
                disabled={saving}
                className="border-[#303036]"
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                disabled={saving || uploading}
                style={{
                  backgroundColor:
                    'var(--cor-primaria, #007AFF)'
                }}
              >
                {saving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                {editing
                  ? 'Salvar alterações'
                  : 'Criar exercício'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
